(function () {
  const STORAGE_KEY = 'stonylab_recently_viewed_products_v1';
  const STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;
  const STORAGE_LIMIT = 20;

  function readStoredProducts() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];

      const cutoff = Date.now() - STORAGE_TTL;
      return parsed.filter(
        (item) => item && item.id && item.handle && item.viewedAt && Number(item.viewedAt) >= cutoff
      );
    } catch (error) {
      return [];
    }
  }

  function writeStoredProducts(products) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(products.slice(0, STORAGE_LIMIT)));
    } catch (error) {
      // Recently viewed remains optional when browser storage is unavailable.
    }
  }

  function recordCurrentProduct() {
    const dataElement = document.querySelector('[data-recently-viewed-current-product]');
    if (!dataElement) return;

    try {
      const product = JSON.parse(dataElement.textContent);
      if (!product?.id || !product?.handle) return;

      const products = readStoredProducts().filter((item) => String(item.id) !== String(product.id));
      products.unshift({ ...product, id: String(product.id), viewedAt: Date.now() });
      writeStoredProducts(products);
    } catch (error) {
      // Invalid product JSON should not affect the product page.
    }
  }

  function parseCartProductIds(section) {
    const dataElement = section.querySelector('[data-cart-product-ids]');
    if (!dataElement) return new Set();

    try {
      return new Set(JSON.parse(dataElement.textContent).map((id) => String(id)));
    } catch (error) {
      return new Set();
    }
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function getProductUrl(product) {
    const path = String(product.url || '');
    if (/^\/products\/[a-z0-9][a-z0-9-]*(?:[/?#]|$)/i.test(path)) return path;

    const handle = String(product.handle || '');
    return /^[a-z0-9][a-z0-9-]*$/i.test(handle) ? `/products/${handle}` : '';
  }

  function getImageUrl(value) {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function appendPrice(container, product) {
    const isOnSale = Number(product.compareAtPrice) > Number(product.price);
    const fromPrefix = product.priceVaries ? 'From ' : '';
    const price = createElement('div', `price${isOnSale ? ' price--on-sale' : ''}`);
    const priceContainer = createElement('div', 'price__container');
    const regular = createElement('div', 'price__regular');
    const regularLabel = createElement('span', 'visually-hidden visually-hidden--inline', 'Regular price');
    const regularValue = createElement(
      'span',
      'price-item price-item--regular',
      `${fromPrefix}${product.priceMoney || ''}`
    );

    regular.append(regularLabel, regularValue);
    priceContainer.appendChild(regular);

    if (isOnSale) {
      const sale = createElement('div', 'price__sale');
      const saleLabel = createElement('span', 'visually-hidden visually-hidden--inline', 'Sale price');
      const saleValue = createElement('span', 'price-item price-item--sale price-item--last');
      saleValue.style.display = 'block';
      saleValue.textContent = `${fromPrefix}${product.priceMoney || ''}`;
      const compareLabel = createElement('span', 'visually-hidden visually-hidden--inline', 'Regular price');
      const compareWrapper = document.createElement('span');
      const compareValue = createElement('s', 'price-item price-item--regular', product.compareAtPriceMoney || '');
      compareWrapper.appendChild(compareValue);
      sale.append(saleLabel, saleValue, compareLabel, compareWrapper);
      priceContainer.appendChild(sale);
    }

    price.appendChild(priceContainer);
    container.appendChild(price);
  }

  function appendRating(container, product) {
    const rating = Number(product.rating);
    const ratingMax = Number(product.ratingMax);
    const ratingCount = Number(product.ratingCount);
    if (!rating || !ratingMax || !ratingCount) return;

    const decimal = rating % 1;
    const roundedDecimal = decimal >= 0.3 && decimal <= 0.7 ? 0.5 : decimal > 0.7 ? 1 : 0;
    const ratingElement = createElement('div', 'rating');
    ratingElement.setAttribute('role', 'img');
    ratingElement.setAttribute('aria-label', `${rating} out of ${ratingMax} stars`);

    const stars = createElement('span', 'rating-star');
    stars.setAttribute('aria-hidden', 'true');
    stars.style.setProperty('--rating', Math.floor(rating));
    stars.style.setProperty('--rating-max', ratingMax);
    stars.style.setProperty('--rating-decimal', roundedDecimal);

    const count = createElement('p', 'rating-count caption');
    const countText = createElement('span', '', `(${ratingCount})`);
    countText.setAttribute('aria-hidden', 'true');
    count.appendChild(countText);
    ratingElement.appendChild(stars);
    container.append(ratingElement, count);
  }

  function createProductCard(product, section) {
    const sectionId = section.dataset.sectionId;
    const productUrl = getProductUrl(product);
    if (!productUrl) return null;

    const cardStyle = section.dataset.cardStyle || 'standard';
    const colorScheme = section.dataset.cardColorScheme || 'background-1';
    const imageUrl = getImageUrl(product.image);
    const imageWidth = Number(product.imageWidth) || 533;
    const imageHeight = Number(product.imageHeight) || imageWidth;
    const ratioPercent = 100;

    const listItem = createElement('li', 'grid__item slider__slide');
    listItem.id = `Slide-${sectionId}-${product.id}`;

    const wrapper = createElement(
      'div',
      'card-wrapper product-card-wrapper underline-links-hover cart-merchandising__recent-card'
    );
    const cardClasses = ['card', `card--${cardStyle}`, imageUrl ? 'card--media' : 'card--text'];
    if (cardStyle === 'card') cardClasses.push(`color-${colorScheme}`, 'gradient');
    const card = createElement('div', cardClasses.join(' '));
    card.style.setProperty('--ratio-percent', `${ratioPercent}%`);

    const innerClasses = ['card__inner', 'ratio'];
    if (cardStyle === 'standard') innerClasses.push(`color-${colorScheme}`, 'gradient');
    const inner = createElement('div', innerClasses.join(' '));
    inner.style.setProperty('--ratio-percent', `${ratioPercent}%`);

    if (imageUrl) {
      const media = createElement('div', 'card__media');
      const mediaInner = createElement('div', 'media media--transparent media--hover-effect');
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = String(product.imageAlt || product.title || '');
      image.className = 'motion-reduce';
      image.loading = 'lazy';
      image.width = imageWidth;
      image.height = imageHeight;
      mediaInner.appendChild(image);
      media.appendChild(mediaInner);
      inner.appendChild(media);
    }

    inner.appendChild(createElement('div', 'card__content'));

    const content = createElement('div', 'card__content');
    const information = createElement('div', 'card__information');
    const heading = createElement('h3', `card__heading${imageUrl || cardStyle === 'standard' ? ' h5' : ''}`);
    heading.id = `title-${sectionId}-${product.id}`;
    const link = createElement('a', 'full-unstyled-link', String(product.title || ''));
    link.href = productUrl;
    link.id = `CardLink-${sectionId}-${product.id}`;
    link.setAttribute('aria-labelledby', heading.id);
    heading.appendChild(link);

    const cardInformation = createElement('div', 'card-information');
    if (section.dataset.showRating === 'true') appendRating(cardInformation, product);
    appendPrice(cardInformation, product);
    information.append(heading, cardInformation);
    content.appendChild(information);
    card.append(inner, content);
    wrapper.appendChild(card);
    listItem.appendChild(wrapper);
    return listItem;
  }

  function renderSection(section) {
    const list = section.querySelector('[data-recently-viewed-list]');
    if (!list) return;

    const cartProductIds = parseCartProductIds(section);
    const maximum = Number(section.dataset.productsToShow) || 10;
    const products = readStoredProducts()
      .filter((product) => product.available !== false && !cartProductIds.has(String(product.id)))
      .slice(0, maximum);

    list.replaceChildren();
    products.forEach((product) => {
      const card = createProductCard(product, section);
      if (card) list.appendChild(card);
    });

    const renderedCount = list.children.length;
    if (!renderedCount) {
      section.hidden = true;
      return;
    }

    section.hidden = false;
    const columns = Number(section.dataset.columnsDesktop) || 5;
    const hasDesktopPages = renderedCount > columns;
    const counter = section.querySelector('.cart-merchandising__page-counter');
    const sliderComponent = section.querySelector('slider-component');
    const sideButtons = section.querySelectorAll('.cart-merchandising__side-button');

    if (counter) counter.hidden = !hasDesktopPages;
    sliderComponent?.classList.toggle('cart-merchandising__slider--with-side-buttons', hasDesktopPages);
    sideButtons.forEach((button) => (button.hidden = !hasDesktopPages));

    window.requestAnimationFrame(() => {
      sliderComponent?.resetPages?.();
    });
  }

  function renderAllSections() {
    document.querySelectorAll('[data-recently-viewed-section]').forEach(renderSection);
  }

  function renderSectionsNearViewport() {
    const sections = [...document.querySelectorAll('[data-recently-viewed-section]')];
    if (!sections.length) return;
    if (!('IntersectionObserver' in window)) {
      window.requestIdleCallback?.(renderAllSections, { timeout: 1200 }) || window.setTimeout(renderAllSections, 0);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const section = entry.target.querySelector?.('[data-recently-viewed-section]');
          if (section) renderSection(section);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '600px 0px' }
    );

    sections.forEach((section) => observer.observe(section.closest('.shopify-section') || section));
  }

  function initialize() {
    recordCurrentProduct();
    renderSectionsNearViewport();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', renderSectionsNearViewport);
  window.addEventListener('pageshow', renderSectionsNearViewport);
})();
